import { useState } from 'react';
import { Clock, Check, Sparkles } from 'lucide-react';
import SectionLabel from '../components/SectionLabel';
import BookingCTA from '../components/BookingCTA';
import SEO from '../components/SEO';
import { useLang } from '../context/LanguageContext';

const Prijzenlijst = () => {
  const { c, ui } = useLang();
  const t = ui.prijzen;
  const [activeTab, setActiveTab] = useState(c.pricing[0].id);
  const active = c.pricing.find(p => p.id === activeTab) || c.pricing[0];

  return (
    <div className="bg-[#0a0a0b] text-white pt-32 pb-24">
      <SEO
        path="/prijzenlijst"
        title="Prijzenlijst — Detailing, Coating & Lakcorrectie prijzen"
        description="Transparante prijzen voor alle detailingbehandelingen: interieur, exterieur, lakcorrectie en pakketten. Vanaf €95 handwas tot €795 keramische coating."
        keywords="detailing prijzen, keramische coating prijs, lakcorrectie kosten, autopoetsen tarief, Deventer"
      />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <SectionLabel>{t.label}</SectionLabel>
        <h1 className="mt-5 text-5xl md:text-7xl font-medium tracking-tight leading-[1.02] max-w-5xl">
          {t.title[0]}<br />{t.title[1]}
        </h1>
        <p className="mt-6 text-white/60 text-lg max-w-3xl leading-relaxed">
          {t.subtitle}
        </p>

        <div className="mt-10 grid md:grid-cols-3 gap-3 max-w-4xl">
          {t.bullets.map(b => (
            <div key={b} className="flex items-start gap-3 p-4 rounded-lg border border-white/10 bg-white/[0.02]">
              <span className="w-6 h-6 rounded-full bg-[#2f6bff]/15 text-[#2f6bff] flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check className="w-3.5 h-3.5" />
              </span>
              <span className="text-white/80 text-sm">{b}</span>
            </div>
          ))}
        </div>

        <div className="mt-16 grid lg:grid-cols-2 gap-6">
          <div className="p-8 rounded-xl border border-white/10 bg-white/[0.02]">
            <h2 className="text-2xl font-medium mb-4">{t.transTitle}</h2>
            <p className="text-white/65 leading-relaxed mb-4">
              {t.transText1}<span className="text-white">{t.transHighlight}</span>{t.transText2}
            </p>
            <ul className="space-y-2 text-white/70 text-sm">
              {t.transList.map(x => (
                <li key={x} className="flex gap-2"><span className="text-[#2f6bff]">•</span> {x}</li>
              ))}
            </ul>
          </div>
          <div className="p-8 rounded-xl border border-white/10 bg-white/[0.02]">
            <h2 className="text-2xl font-medium mb-4">{t.zakTitle}</h2>
            <p className="text-white/65 leading-relaxed mb-4">
              {t.zakText}
            </p>
            <ul className="space-y-2 text-white/70 text-sm">
              {t.zakList.map(x => (
                <li key={x} className="flex gap-2"><span className="text-[#2f6bff]">•</span> {x}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-20">
          <SectionLabel>{t.listLabel}</SectionLabel>
          <div className="mt-6 flex flex-wrap gap-2 border-b border-white/10 pb-1">
            {c.pricing.map(p => (
              <button
                key={p.id}
                onClick={() => setActiveTab(p.id)}
                className={`px-5 py-3 text-sm transition-colors border-b-2 -mb-px ${
                  activeTab === p.id
                    ? 'text-white border-[#2f6bff]'
                    : 'text-white/50 border-transparent hover:text-white'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {active.intro && <p className="mt-8 text-white/60 max-w-3xl">{active.intro}</p>}

          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {active.items.map((item, i) => (
              <article
                key={i}
                className="rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.03] transition-colors overflow-hidden flex flex-col"
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 px-3 py-1.5 rounded-md bg-black/60 backdrop-blur text-white text-xs flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    {item.time}
                  </span>
                </div>

                <div className="p-7 flex-1 flex flex-col">
                  <h3 className="text-2xl font-medium leading-tight text-white">{item.title}</h3>
                  <p className="mt-3 text-white/65 text-sm leading-relaxed">{item.desc}</p>

                  {item.bullets && (
                    <div className="mt-5">
                      <div className="text-white/50 text-[11px] tracking-[0.25em] uppercase mb-2">{t.included}</div>
                      <ul className="space-y-1.5">
                        {item.bullets.map((b, k) => (
                          <li key={k} className="flex items-start gap-2 text-white/75 text-sm">
                            <span className="text-[#2f6bff] mt-0.5">•</span>
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-5">
                    <div className="text-white/50 text-[11px] tracking-[0.25em] uppercase mb-2">{t.suitableFor}</div>
                    <ul className="space-y-1.5">
                      {item.suitableFor.map((s, k) => (
                        <li key={k} className="flex items-start gap-2 text-white/85 text-sm">
                          <span className="mt-1 w-4 h-4 rounded-full bg-[#2f6bff]/20 text-[#2f6bff] flex items-center justify-center flex-shrink-0">
                            <Check className="w-2.5 h-2.5" />
                          </span>
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-5 p-4 rounded-lg bg-[#2f6bff]/8 border border-[#2f6bff]/20">
                    <div className="flex items-center gap-2 text-[#7aa3ff] text-[11px] tracking-[0.25em] uppercase mb-1.5">
                      <Sparkles className="w-3.5 h-3.5" />
                      {t.result}
                    </div>
                    <p className="text-white/85 text-sm leading-relaxed">{item.result}</p>
                  </div>

                  <div className="mt-6 pt-5 border-t border-white/5 flex items-end justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-white/40 text-[11px] tracking-wider uppercase">{t.price}</div>
                      <div className="mt-1 text-2xl font-medium text-white">{item.price}</div>
                    </div>
                    <BookingCTA
                      size="sm"
                      waMessage={t.waQuote(item.title)}
                      offerteTreatment={item.title}
                    />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="mt-16 p-6 rounded-xl border border-white/10 bg-white/[0.02] text-white/55 text-sm italic max-w-4xl">
          {t.footerNote}
        </div>

        <div className="mt-16 text-center">
          <div className="flex justify-center">
            <BookingCTA size="lg" align="center" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Prijzenlijst;
